import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, UserPlus, ArrowRight, ArrowLeft, Building2, CheckCircle2, Package, TrendingUp, ShieldCheck, Zap, ScanLine } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authApi, MIN_PASSWORD_LENGTH, PASSWORD_TOO_SHORT } from '../services/api';
import FullFixLogo from '../assets/FullFixLogo';
import { useBrand } from '../contexts/BrandContext';
import './LoginPage.css';

function LoginPage() {
  // Бренд склада: у Full-Fix default — экран как был; у склада облака — его логотип/название
  const { brand } = useBrand();
  const BrandMark = ({ light, height }) => (brand.default
    ? <FullFixLogo height={height} light={light} />
    : brand.logo_url
      ? <img src={brand.logo_url} alt={brand.product_name} className="login-brand-img" style={{ maxHeight: height }} />
      : <span className={`login-brand-name ${light ? 'login-brand-name--light' : ''}`}>{brand.product_name}</span>);
  const [mode, setMode] = useState('login');
  const [regStep, setRegStep] = useState(1);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [regCompany, setRegCompany] = useState('');
  const [regInn, setRegInn] = useState('');
  const [regName, setRegName] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;     // зажатый Enter слал десяток попыток за секунду и запирал вход (08.10)
    setError('');
    setLoading(true);
    try {
      await login(username.trim(), password);
      // На домашний экран роли (HomeRedirect): раньше всех вели на /dashboard,
      // а складу без раздела 'processing' он закрыт
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || err.friendlyMessage || 'Неверный логин или пароль');
    } finally {
      setLoading(false);
    }
  };

  const handleRegStep1 = () => {
    if (!regCompany.trim()) { setError('Укажите название компании'); return; }
    setError('');
    setRegStep(2);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName.trim() || !regUsername.trim() || !regPassword.trim()) {
      setError('Заполните имя, логин и пароль'); return;
    }
    if (regPassword.length < MIN_PASSWORD_LENGTH) { setError(PASSWORD_TOO_SHORT); return; }
    setError('');
    setLoading(true);
    try {
      await authApi.register({
        company_name: regCompany.trim(),
        username: regUsername.trim(),
        password: regPassword,
        name: regName.trim(),
        inn: regInn || null,
      });
      await login(regUsername.trim(), regPassword);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || err.friendlyMessage || 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  const switchToLogin = () => { setMode('login'); setError(''); };

  return (
    <div className={`login-page ${brand.default ? '' : 'login-page--brand'}`}>
      <div className="login-left">
        <div className="login-left-content">
          <div className="login-brand">
            <BrandMark height={48} light />
            <div className="login-brand-divider" />
            <p className="login-brand-subtitle">{brand.login_subtitle || 'Система управления складом'}</p>
          </div>

          <div className="login-hero">
            {brand.default
              ? <h2>Управляйте складом<br /><span className="hero-accent">без хаоса</span></h2>
              : <h2>{brand.login_title}</h2>}
            <p className="login-hero-desc">
              Полный контроль от приёмки до отгрузки. Маркировка, сборка заказов, интеграция с маркетплейсами — всё в одном месте.
            </p>
          </div>

          <div className="login-features">
            <div className="login-feature">
              <div className="feature-icon"><Package size={20} /></div>
              <div>
                <strong>Маркетплейсы</strong>
                <span>Wildberries и Ozon — ФБО и ФБС</span>
              </div>
            </div>
            <div className="login-feature">
              <div className="feature-icon"><Zap size={20} /></div>
              <div>
                <strong>Маркировка</strong>
                <span>Честный знак, коды ЧЗ, DataMatrix</span>
              </div>
            </div>
            <div className="login-feature">
              <div className="feature-icon"><TrendingUp size={20} /></div>
              <div>
                <strong>Аналитика</strong>
                <span>Дашборды и отчёты в реальном времени</span>
              </div>
            </div>
            <div className="login-feature">
              <div className="feature-icon"><ShieldCheck size={20} /></div>
              <div>
                <strong>Надёжность</strong>
                <span>Полный аудит всех операций</span>
              </div>
            </div>
          </div>
        </div>

        <div className="login-left-footer">
          {brand.default
            ? <span>full-fix.ru</span>
            : <span>{brand.support_contact || ''}{brand.powered_by && (
                <a className="login-powered" href={brand.powered_by_url} target="_blank" rel="noopener noreferrer">
                  {brand.support_contact ? ' · ' : ''}{brand.powered_by_text}
                </a>)}</span>}
        </div>

        {/* Decorative elements */}
        <div className="login-decor login-decor-1" />
        <div className="login-decor login-decor-2" />
        <div className="login-decor login-decor-3" />
      </div>

      <div className="login-right">
        <div className="login-card">
          <div className="login-mobile-logo">
            <BrandMark height={36} />
          </div>

          {mode === 'login' ? (
            <>
              <div className="login-header">
                <h2>Вход в систему</h2>
                <p>Введите данные для входа</p>
              </div>
              <form onSubmit={handleSubmit} className="login-form">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-group">
                  <label className="form-label">Логин</label>
                  <input type="text" className="form-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Введите логин" required autoFocus autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" />
                </div>
                <div className="form-group">
                  <label className="form-label">Пароль</label>
                  <div className="password-wrapper">
                    <input type={showPassword ? 'text' : 'password'} className="form-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Введите пароль" required />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                  {loading ? <span className="spinner" /> : <LogIn size={18} />}
                  Войти
                </button>
              </form>
              {/* Сотрудники склада входят по бейджу — ссылка прямо на форме, а не набором
                  /badge руками (просьба владельца 25.09) */}
              <button type="button" className="btn btn-outline login-btn login-btn--outline"
                      onClick={() => navigate('/badge')}>
                <ScanLine size={18} /> Войти по бейджу
              </button>
              {/* Регистрация только по приглашению (решение владельца 24.09.2026):
                  кабинет клиенту заводит менеджер склада */}
              <div className="login-switch">
                <span>{brand.default
                  ? 'Нет аккаунта? Кабинет выдаёт менеджер Full-Fix — напишите нам.'
                  : `Нет аккаунта? Кабинет выдаёт менеджер склада${brand.support_contact ? `: ${brand.support_contact}` : ''}.`}</span>
              </div>
            </>
          ) : (
            <>
              <div className="login-header">
                <h2>{regStep === 1 ? 'Регистрация' : 'Создание аккаунта'}</h2>
                <p>{regStep === 1 ? 'Шаг 1: Укажите компанию' : 'Шаг 2: Данные для входа'}</p>
              </div>
              {error && <div className="alert alert-error">{error}</div>}

              {regStep === 1 ? (
                <div className="login-form">
                  <div className="form-group">
                    <label className="form-label">Название компании *</label>
                    <input type="text" className="form-input" value={regCompany} onChange={e => setRegCompany(e.target.value)} placeholder="ООО «Компания» или ИП Иванов" autoFocus onKeyDown={e => e.key === 'Enter' && handleRegStep1()} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ИНН</label>
                    <input type="text" className="form-input" value={regInn} onChange={e => setRegInn(e.target.value)} placeholder="Необязательно" />
                  </div>
                  <button type="button" className="btn btn-primary login-btn" onClick={handleRegStep1} disabled={!regCompany.trim()}>
                    Далее <ArrowRight size={18} />
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRegister} className="login-form">
                  <div className="reg-company-badge">
                    <Building2 size={14} />
                    <span>{regCompany}</span>
                    <CheckCircle2 size={14} className="reg-check-icon" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ваше имя *</label>
                    <input type="text" className="form-input" value={regName} onChange={e => setRegName(e.target.value)} placeholder="Иван Иванов" autoFocus />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Логин *</label>
                    <input type="text" className="form-input" value={regUsername} onChange={e => setRegUsername(e.target.value)} placeholder="Придумайте логин" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Пароль *</label>
                    <div className="password-wrapper">
                      <input type={showRegPassword ? 'text' : 'password'} className="form-input" value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder={`Мин. ${MIN_PASSWORD_LENGTH} символов`} />
                      <button type="button" className="password-toggle" onClick={() => setShowRegPassword(!showRegPassword)} tabIndex={-1}>
                        {showRegPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="login-btn-row">
                    <button type="button" className="btn btn-outline login-back-btn" onClick={() => setRegStep(1)}>
                      <ArrowLeft size={16} />
                    </button>
                    <button type="submit" className="btn btn-primary login-btn" disabled={loading || !regName.trim() || !regUsername.trim() || !regPassword.trim()}>
                      {loading ? <span className="spinner" /> : <UserPlus size={18} />}
                      Зарегистрироваться
                    </button>
                  </div>
                </form>
              )}
              <div className="login-switch">
                <span>Уже есть аккаунт?</span>
                <button className="login-switch-btn" onClick={switchToLogin}>
                  <LogIn size={15} /> Войти
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
